import { useCallback } from "react";
import type { AlertsState, ArgoTsVersionState, LatestTags, OverviewCluster } from "../api/types";
import { useSort } from "../hooks/useSort";
import { behindLatest } from "../util/versions";
import AlertBadge from "./AlertBadge";
import VersionChip from "./VersionChip";

function argotsState(c: OverviewCluster): ArgoTsVersionState | null {
    return c.state.fleet === "argots" ? c.state : null;
}

const COLUMNS: { col: string; label: string; sortable: boolean }[] = [
    { col: "status", label: "Status", sortable: false },
    { col: "cluster", label: "Cluster", sortable: true },
    { col: "infra", label: "Infra version", sortable: true },
    { col: "appInfra", label: "App infra version", sortable: true },
    { col: "argo", label: "Argo revision", sortable: true },
    { col: "alerts", label: "Alerts", sortable: false },
    { col: "links", label: "Links", sortable: false },
];

export default function IntegrationTable({
    clusters,
    latestTags,
    alerts,
}: {
    clusters: OverviewCluster[];
    latestTags: LatestTags | null;
    alerts: AlertsState;
}) {
    const sortVal = useCallback((c: OverviewCluster, col: string): string => {
        const s = argotsState(c);
        switch (col) {
            case "infra":
                return s?.infraVersion?.version ?? s?.infraVersion?.raw ?? "";
            case "appInfra":
                return s?.appInfraVersion?.version ?? s?.appInfraVersion?.raw ?? "";
            case "argo":
                return s?.argoApp?.targetRevision ?? "";
            default:
                return c.cluster;
        }
    }, []);
    const { sorted, toggle, arrow } = useSort(clusters, sortVal, "cluster");
    const latest = latestTags?.components["argocd-ts-cmp"]?.[0];

    return (
        <div className="table-wrap">
            <table>
                <thead>
                    <tr>
                        {COLUMNS.map((c) => (
                            <th key={c.col} className={c.sortable ? "sortable" : ""} onClick={c.sortable ? () => toggle(c.col) : undefined}>
                                {c.label}
                                {c.sortable ? <span className="sort-arrow">{arrow(c.col)}</span> : null}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {sorted.map((c) => (
                        <IntegrationRow key={c.cluster} c={c} latest={latest} alerts={alerts} />
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function IntegrationRow({ c, latest, alerts }: { c: OverviewCluster; latest: string | undefined; alerts: AlertsState }) {
    const s = argotsState(c);
    const alert = alerts.summaries.find((a) => a.cluster === c.cluster);
    const links: { label: string; url: string }[] = [];
    if (c.grafanaUrl) links.push({ label: "grafana", url: c.grafanaUrl });
    if (c.radarUrl) links.push({ label: "radar", url: c.radarUrl });
    if (s?.infraVersion?.sourceUrl) links.push({ label: "infra", url: s.infraVersion.sourceUrl });
    if (s?.appInfraVersion?.sourceUrl) links.push({ label: "app", url: s.appInfraVersion.sourceUrl });
    if (s?.argoApp?.sourceUrl) links.push({ label: "argo", url: s.argoApp.sourceUrl });

    return (
        <tr>
            <td>
                <span className={`status-dot ${c.status}`} title={c.lastError ?? c.status} />
                {c.stale ? (
                    <span className="stale-badge" title={`fetched ${c.fetchedAt}`}>
                        stale
                    </span>
                ) : null}
            </td>
            <td>
                <strong>{c.cluster}</strong>
                {c.cloud ? <span className={`cloud-badge ${c.cloud}`}>{c.cloud.toUpperCase()}</span> : null}
            </td>
            <VersionCell vref={s?.infraVersion} latest={latest} />
            <VersionCell vref={s?.appInfraVersion} latest={latest} />
            <td className="version">{s?.argoApp?.targetRevision ?? <span className="muted">—</span>}</td>
            <td>{alert && alert.total > 0 ? <AlertBadge summary={alert} /> : <span className="muted">—</span>}</td>
            <td>
                {links.length > 0
                    ? links.map((l, i) => (
                          <span key={l.label}>
                              {i > 0 ? " · " : ""}
                              <a className="source-link" href={l.url} target="_blank" rel="noopener noreferrer">
                                  {l.label}
                              </a>
                          </span>
                      ))
                    : "—"}
            </td>
        </tr>
    );
}

function VersionCell({ vref, latest }: { vref?: { raw?: string; version?: string }; latest: string | undefined }) {
    if (!vref || (!vref.version && !vref.raw)) return <td className="version muted">—</td>;
    const ver = vref.version ?? vref.raw;
    return (
        <td className="version">
            <VersionChip version={ver} behind={behindLatest(vref.version ?? vref.raw, latest)} title={vref.raw} />
        </td>
    );
}
